import { buildStandardClientHeaders } from './request-context'

import type { ApiRequestContext } from './request-context'
import type { ApiError, ApiResponse } from './types'

interface ApiRequestOptions {
  body?: unknown
  headers?: Record<string, string>
}

export interface ApiClientConfig {
  /** 品牌 API 地址 (如 api.visiva.ai) */
  baseURL: string
  timeout?: number
  requestContext?: ApiRequestContext
  headers?: Record<string, string>
}

export class ApiClient {
  private baseURL: string
  private timeout: number
  private headers: Record<string, string>
  private requestContext?: ApiRequestContext

  constructor(config: ApiClientConfig) {
    this.baseURL = config.baseURL.replace(/\/$/, '')
    this.timeout = config.timeout ?? 30000
    this.headers = {
      'Content-Type': 'application/json',
      ...config.headers,
    }
    this.requestContext = config.requestContext
  }

  async request<T>(
    method: string,
    path: string,
    options?: ApiRequestOptions
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${path}`
    const standardHeaders = this.requestContext
      ? await buildStandardClientHeaders(this.requestContext)
      : {}

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    try {
      const response = await fetch(url, {
        method,
        headers: {
          ...this.headers,
          ...standardHeaders,
          ...options?.headers,
        },
        body: options?.body ? JSON.stringify(options.body) : undefined,
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        const error: ApiError = {
          message: `HTTP ${response.status}: ${response.statusText}`,
          status: response.status,
        }
        let errorData: unknown

        try {
          errorData = await response.json()
          const errorBody = errorData as {
            message?: string
            msg?: string
            code?: number | string
            error?: {
              message?: string
              code?: number | string
            }
          }
          // 支持 message 和 msg 两种字段名
          const errorMsg = errorBody.error?.message || errorBody.message || errorBody.msg
          if (errorMsg) {
            error.message = errorMsg
          }
          const errorCode = errorBody.error?.code ?? errorBody.code
          if (errorCode !== undefined) {
            error.code = String(errorCode)
          }
        } catch {
          // Ignore JSON parse error
        }

        return errorData === undefined ? { error } : { data: errorData as T, error }
      }

      if (response.status === 204) {
        return { data: undefined }
      }

      return { data: (await response.json()) as T }
    } catch (err) {
      clearTimeout(timeoutId)

      const error: ApiError = {
        message: err instanceof Error ? err.message : 'Unknown error',
      }

      if (err instanceof Error && err.name === 'AbortError') {
        error.message = 'Request timeout'
        error.code = 'TIMEOUT'
      }

      return { error }
    }
  }

  async get<T>(path: string, options?: Omit<ApiRequestOptions, 'body'>): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, options)
  }

  async post<T>(
    path: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, 'body'>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, { ...options, body })
  }

  async put<T>(
    path: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, 'body'>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PUT', path, { ...options, body })
  }

  async patch<T>(
    path: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, 'body'>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, { ...options, body })
  }

  async delete<T>(
    path: string,
    options?: Omit<ApiRequestOptions, 'body'>
  ): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, options)
  }
}

let apiClient: ApiClient | null = null

export function initApiClient(config: ApiClientConfig): ApiClient {
  apiClient = new ApiClient(config)
  return apiClient
}

export function getApiClient(): ApiClient {
  if (!apiClient) {
    throw new Error('API client not initialized. Call initApiClient first.')
  }
  return apiClient
}
